import type { ReactElement, ReactNode } from 'react';
import { Dialog as AriaDialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { BottomSheet } from './BottomSheet';
import { Button } from './Button';
import { overlayBodyClass } from './overlay';
import { useIsPhone } from './useIsPhone';

export interface DialogProps {
  title: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  footer?: ReactNode;
  children: ReactNode;
}

// Arbitrary values are allowed inside ui/.
const WIDTH = { sm: 'w-[440px]', md: 'w-[640px]', lg: 'w-[880px]' } as const;

export function Dialog({
  title,
  isOpen,
  onOpenChange,
  size = 'md',
  footer,
  children
}: DialogProps): ReactElement {
  // Phones get the same content as a bottom sheet: reachable with a thumb and
  // never narrower than the screen.
  const phone = useIsPhone();
  if (phone) {
    return (
      <BottomSheet title={title} isOpen={isOpen} onOpenChange={onOpenChange} footer={footer}>
        {children}
      </BottomSheet>
    );
  }
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className="fixed inset-0 z-dialog m-0 px-0 py-4 border-0 bg-scrim flex items-center justify-center"
    >
      {/* Capped to the overlay (less its 16px top and bottom): a taller dialog scrolls its body. */}
      <Modal
        className={`${WIDTH[size]} max-w-[92vw] max-h-full flex flex-col m-0 p-0 border-0 bg-surface text-ink font-ui rounded-panel shadow-dialog overflow-hidden`}
      >
        <AriaDialog className="m-0 p-6 border-0 bg-surface outline-none flex flex-col gap-4 min-h-0 font-ui">
          <Heading slot="title" className="m-0 p-0 text-xl font-semibold font-ui text-ink">
            {title}
          </Heading>
          <div className={overlayBodyClass}>{children}</div>
          {footer ? <div className="m-0 p-0 flex justify-end gap-2">{footer}</div> : null}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  onConfirm(): void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  tone = 'primary',
  isOpen,
  onOpenChange,
  onConfirm
}: ConfirmDialogProps): ReactElement {
  return (
    <Dialog
      title={title}
      size="sm"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      footer={
        <>
          <Button variant="secondary" autoFocus onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={tone}
            onPress={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="m-0 p-0 text-md font-ui text-ink">{message}</p>
    </Dialog>
  );
}
